import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from './auth-context';
import { CompanyContext } from './company-context';

const STORAGE_KEY = 'crm.company';

function readStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Selector global de empresa: las páginas parten de la empresa activa salvo que la URL diga otra cosa. */
export function CompanyProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const companies = useMemo(
    () =>
      user?.memberships.filter(
        (m, i, a) =>
          m.companyActive !== false && a.findIndex((x) => x.companyId === m.companyId) === i,
      ) ?? [],
    [user],
  );
  const [stored, setStored] = useState<string | null>(readStored);
  const single = companies.length === 1 ? companies[0]?.companyId : undefined;
  const active =
    single ??
    (stored && companies.some((c) => c.companyId === stored) ? stored : companies[0]?.companyId);
  useEffect(() => {
    if (!user) return;
    if (stored && !companies.some((c) => c.companyId === stored)) setStored(null);
  }, [user, stored, companies]);
  const setActive = useCallback((companyId: string) => {
    setStored(companyId);
    try {
      localStorage.setItem(STORAGE_KEY, companyId);
    } catch {
      /* sin almacenamiento */
    }
  }, []);
  const value = useMemo(
    () => ({ companies, single, active, setActive }),
    [companies, single, active, setActive],
  );
  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}
